import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import { useUI } from '../contexts/UIContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

const fmtDataHora = (d: any) => d ? new Date(d).toLocaleString('pt-BR') : '—'
function hojeISO() { return new Date().toISOString().slice(0, 10) }
function seteDiasAtrasISO() { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10) }

export default function AreasVisitadas() {
  const { toast } = useUI()
  const [clientes, setClientes] = useState<any[]>([])
  const [clienteId, setClienteId] = useState('')
  const [ativos, setAtivos] = useState<any[]>([])
  const [ativoId, setAtivoId] = useState('')
  const [dataInicio, setDataInicio] = useState(seteDiasAtrasISO())
  const [dataFim, setDataFim] = useState(hojeISO())
  const [dados, setDados] = useState<any>(null)
  const [carregando, setCarregando] = useState(false)
  const [exportando, setExportando] = useState<'excel' | 'pdf' | null>(null)
  const [visualizacao, setVisualizacao] = useState<'lista' | 'mapa'>('lista')

  const token = localStorage.getItem('moobi_token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    axios.get(`${API}/clientes`, { headers }).then(r => setClientes(r.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = clienteId ? { cliente_id: clienteId } : {}
    axios.get(`${API}/ativos`, { headers, params }).then(r => {
      setAtivos(r.data)
      setAtivoId('')
      setDados(null)
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  const buscar = useCallback(async () => {
    if (!ativoId) return
    setCarregando(true)
    try {
      const { data } = await axios.get(`${API}/relatorios/areas-visitadas`, {
        headers, params: { ativo_id: ativoId, dataInicio, dataFim },
      })
      setDados(data)
    } catch {
      toast('Erro ao carregar relatório', 'error')
    } finally {
      setCarregando(false)
    }
  }, [ativoId, dataInicio, dataFim]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (ativoId) buscar() }, [ativoId, buscar])

  const exportar = async (formato: 'excel' | 'pdf') => {
    if (!ativoId) return
    setExportando(formato)
    try {
      const { data, headers: respHeaders } = await axios.get(`${API}/relatorios/areas-visitadas/exportar`, {
        headers, params: { ativo_id: ativoId, dataInicio, dataFim, formato },
        responseType: 'blob',
      })
      const blob = new Blob([data], { type: String(respHeaders['content-type'] || 'application/octet-stream') })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `areas_visitadas_${Date.now()}.${formato === 'excel' ? 'xlsx' : 'pdf'}`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      toast('Erro ao exportar', 'error')
    } finally {
      setExportando(null)
    }
  }

  const marcadores: { lat: number; lng: number; label: string; cor: string }[] = []
  if (dados) {
    for (const p of dados.pontos) {
      if (p.latitude && p.longitude) marcadores.push({ lat: p.latitude, lng: p.longitude, label: `📍 ${p.ponto_nome}`, cor: '#38bdf8' })
    }
    for (const c of dados.cercas) {
      if (c.latitude && c.longitude) marcadores.push({ lat: c.latitude, lng: c.longitude, label: `${c.tipo_alerta === 'entrada_cerca' ? '🟢 Entrada' : '🔴 Saída'} de cerca`, cor: c.tipo_alerta === 'entrada_cerca' ? '#4ade80' : '#f87171' })
    }
  }

  return (
    <div style={{ padding: 24, height: '100%', overflowY: 'auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="ph-bold ph-map-trifold" style={{ color: '#0ea5e9' }} />
        Relatório de Áreas Visitadas
      </h1>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Cliente</div>
          <select value={clienteId} onChange={e => setClienteId(e.target.value)}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, padding: '7px 9px' }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c: any) => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Ativo</div>
          <select value={ativoId} onChange={e => setAtivoId(e.target.value)}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, padding: '7px 9px' }}>
            <option value="">— selecione —</option>
            {ativos.map((a: any) => <option key={a.id} value={a.id}>{a.nome}{a.placa ? ` · ${a.placa}` : ''}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>De</div>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, padding: '6px 8px' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Até</div>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, padding: '6px 8px' }} />
        </div>
        <button onClick={buscar} disabled={!ativoId}
          style={{ background: '#1e3a6e', border: '1px solid #2d4166', borderRadius: 6, color: '#38bdf8', cursor: ativoId ? 'pointer' : 'not-allowed', fontSize: 13, padding: '7px 14px', opacity: ativoId ? 1 : 0.5 }}>
          <i className="ph-bold ph-magnifying-glass" style={{ marginRight: 5 }} />Buscar
        </button>
        <div style={{ flex: 1 }} />
        {dados && (
          <>
            <button onClick={() => exportar('excel')} disabled={exportando !== null}
              style={{ background: '#14532d', border: '1px solid #166534', borderRadius: 6, color: '#4ade80', cursor: exportando ? 'default' : 'pointer', fontSize: 12, padding: '7px 12px' }}>
              <i className="ph-bold ph-file-xls" style={{ marginRight: 5 }} />{exportando === 'excel' ? 'Gerando...' : 'Excel'}
            </button>
            <button onClick={() => exportar('pdf')} disabled={exportando !== null}
              style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, color: '#f87171', cursor: exportando ? 'default' : 'pointer', fontSize: 12, padding: '7px 12px' }}>
              <i className="ph-bold ph-file-pdf" style={{ marginRight: 5 }} />{exportando === 'pdf' ? 'Gerando...' : 'PDF'}
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setVisualizacao('lista')}
                style={{ background: visualizacao === 'lista' ? '#1e3a6e' : 'transparent', border: '1px solid #2d4166', borderRadius: 6, color: visualizacao === 'lista' ? '#38bdf8' : '#94a3b8', cursor: 'pointer', fontSize: 12, padding: '6px 12px' }}>
                Lista
              </button>
              <button onClick={() => setVisualizacao('mapa')}
                style={{ background: visualizacao === 'mapa' ? '#1e3a6e' : 'transparent', border: '1px solid #2d4166', borderRadius: 6, color: visualizacao === 'mapa' ? '#38bdf8' : '#94a3b8', cursor: 'pointer', fontSize: 12, padding: '6px 12px' }}>
                Mapa
              </button>
            </div>
          </>
        )}
      </div>

      {!ativoId && (
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '60px 0' }}>
          Selecione um ativo para ver as áreas visitadas
        </div>
      )}

      {carregando && <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 0' }}>Carregando...</div>}

      {!carregando && dados && visualizacao === 'lista' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          {/* Pontos de Interesse */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="ph-bold ph-map-pin" />Pontos de Interesse ({dados.pontos.length})
            </div>
            {dados.pontos.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>Nenhuma visita registrada no período</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {dados.pontos.map((p: any) => (
                  <div key={p.id} style={{ borderBottom: '1px solid #1e293b', paddingBottom: 6 }}>
                    <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{p.ponto_nome}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {fmtDataHora(p.chegada)} {p.saida ? `→ ${fmtDataHora(p.saida)}` : '(em andamento)'}
                      {p.duracao_min != null && ` · ${p.duracao_min} min`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cercas */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#4ade80', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="ph-bold ph-polygon" />Cercas Virtuais ({dados.cercas.length})
            </div>
            {dados.cercas.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>Nenhum evento de cerca no período</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {dados.cercas.map((c: any) => (
                  <div key={c.id} style={{ borderBottom: '1px solid #1e293b', paddingBottom: 6 }}>
                    <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>
                      {c.tipo_alerta === 'entrada_cerca' ? '🟢 Entrada' : '🔴 Saída'} — {c.descricao || 'Cerca'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{fmtDataHora(c.criado_em)}{c.endereco ? ` · ${c.endereco}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cidades */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fbbf24', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="ph-bold ph-city" />Cidades percorridas ({dados.cidades.length})
            </div>
            {dados.cidades.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>Nenhum dado de localização no período</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {dados.cidades.map((c: any) => (
                  <div key={c.cidade} style={{ borderBottom: '1px solid #1e293b', paddingBottom: 6 }}>
                    <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{c.cidade}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {fmtDataHora(c.primeiraVez)} → {fmtDataHora(c.ultimaVez)} · {c.ocorrencias} registro(s)
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!carregando && dados && visualizacao === 'mapa' && (
        <div style={{ height: 500, borderRadius: 10, overflow: 'hidden', border: '1px solid #1e293b' }}>
          {marcadores.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 13, background: '#0f172a' }}>
              Nenhuma coordenada disponível para exibir no mapa neste período
            </div>
          ) : (
            <MapContainer center={[marcadores[0].lat, marcadores[0].lng]} zoom={12} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {marcadores.map((m, i) => (
                <Marker key={i} position={[m.lat, m.lng]}>
                  <Popup>{m.label}</Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      )}
    </div>
  )
}
